// ============================================================================
// AddVideoDialog — add a single video to an existing project (Iter30)
// Insertion modes: auto_append | specific_date | no_date.
// The created task is a top-level video task (kind='video') which renders the
// standard 4-block workflow in DetailSidebar (consignes/tech/concept/publi).
// ============================================================================
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { X, Loader2, Sparkles } from './icons';

const FORMATS = [
  { id: 'podcast', label: 'Podcast' },
  { id: 'facecam', label: 'Facecam' },
  { id: 'short', label: 'Short' },
  { id: 'special', label: 'Spécial' },
];

export default function AddVideoDialog({ project, onClose, onSaved }) {
  const [title, setTitle] = useState('');
  const [format, setFormat] = useState('special');
  const [insertMode, setInsertMode] = useState('auto_append');
  const [specificDate, setSpecificDate] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async () => {
    if (insertMode === 'specific_date' && !specificDate) {
      toast.error('Date requise');
      return;
    }
    setBusy(true);
    try {
      const body = {
        title: title.trim() || undefined,
        format, insert_mode: insertMode,
      };
      if (insertMode === 'specific_date') body.due_date = specificDate;
      const r = await api.post(`/projects/${project.id}/videos`, body);
      toast.success(`Vidéo créée${r.data.due_date ? ` · ${r.data.due_date.slice(0, 10)}` : ' (sans date)'}`);
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création vidéo');
    } finally {
      setBusy(false);
    }
  };

  const hasProjectDates = project.start_date && project.end_date;

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(10,26,51,0.70)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '24px 18px', overflowY: 'auto',
    }}>
      <div onClick={(e) => e.stopPropagation()} className="scale-in" style={{
        width: '100%', maxWidth: 520,
        background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 22, padding: 24,
        margin: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
          <h2 className="asa-heading" style={{ margin: 0, fontSize: 'var(--text-xl)', flex: 1 }}>
            Ajouter une vidéo
          </h2>
          <button data-testid="add-video-close" onClick={onClose}><X size={18} color="var(--t3)" /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div className="asa-eyebrow" style={{ marginBottom: 7 }}>Titre (optionnel)</div>
            <div style={{ padding: '11px 14px', background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12 }}>
              <input
                data-testid="add-video-title"
                autoFocus value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Sera 'Vidéo N — projet' si vide"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div>
            <div className="asa-eyebrow" style={{ marginBottom: 7 }}>Format</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {FORMATS.map((f) => (
                <button
                  key={f.id} type="button"
                  data-testid={`add-video-format-${f.id}`}
                  onClick={() => setFormat(f.id)}
                  style={{
                    padding: '7px 14px', borderRadius: 999,
                    background: format === f.id ? 'rgba(167,139,250,0.18)' : 'transparent',
                    border: format === f.id ? '1.5px solid #A78BFA' : '1.5px solid var(--bd)',
                    color: format === f.id ? '#A78BFA' : 'var(--t2)',
                    fontFamily: 'Sora', fontWeight: 600, fontSize: 12, cursor: 'pointer',
                  }}
                >{f.label}</button>
              ))}
            </div>
          </div>

          <div>
            <div className="asa-eyebrow" style={{ marginBottom: 7 }}>Date de publication</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {[
                {
                  id: 'auto_append', label: 'Auto · à la suite',
                  hint: hasProjectDates
                    ? `Insérée à la suite des vidéos existantes (cadence ${project.cadence_days || 1} jour${(project.cadence_days || 1) > 1 ? 's' : ''})`
                    : 'Le projet n\'a pas de dates → la vidéo sera sans date',
                },
                { id: 'specific_date', label: 'Date précise', hint: 'Je choisis le jour exact' },
                { id: 'no_date', label: 'Pas de date', hint: 'Je replanifierai plus tard' },
              ].map((m) => (
                <button
                  key={m.id}
                  data-testid={`add-video-mode-${m.id}`}
                  type="button"
                  onClick={() => setInsertMode(m.id)}
                  style={{
                    textAlign: 'left', padding: '10px 13px',
                    background: insertMode === m.id ? 'rgba(91,190,252,0.10)' : 'var(--sf2)',
                    border: insertMode === m.id ? '1.5px solid #5BBEFC' : '1.5px solid var(--bd)',
                    borderRadius: 11, cursor: 'pointer',
                  }}
                >
                  <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, color: 'var(--t1)' }}>{m.label}</div>
                  <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)', marginTop: 2 }}>{m.hint}</div>
                </button>
              ))}
            </div>
          </div>

          {insertMode === 'specific_date' && (
            <div>
              <div className="asa-eyebrow" style={{ marginBottom: 7 }}>Jour de publication</div>
              <div style={{ padding: '11px 14px', background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12 }}>
                <input
                  data-testid="add-video-specific-date"
                  type="date" value={specificDate}
                  onChange={(e) => setSpecificDate(e.target.value)}
                  style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.88rem', colorScheme: 'dark' }}
                />
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button type="button" onClick={onClose} className="asa-btn-ghost asa-btn" style={{ flex: 1 }}>Annuler</button>
          <button
            data-testid="add-video-submit"
            type="button" onClick={submit} disabled={busy}
            className="asa-btn"
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            {busy ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />}
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}
